import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { PessoaImpressaoAgrupadaRequest } from '../../requests/pessoa/pessoa-impressao-agrupado.request';
import { PessoaImpressaoAniversarianteRequest } from '../../requests/pessoa/pessoa-impressao-aniversariante.request';
import { PessoaListaRequest } from '../../requests/pessoa/pessoa-lista.request';
import { GetImpressaoPessoaAgrupadaResponse } from '../../responses/pessoa/get-impressao-pessoa-agrupada.response';
import { GetImpressaoPessoaSimplesResponse } from '../../responses/pessoa/get-impressao-pessoa-simples.response';
import { MunicipioService } from '../usuario/municipio.service';


@Injectable({
  providedIn: 'root',
})
export class ImpressaoPessoaService extends CommonService {
  constructor(
    private httpClient: HttpClient,
    private municipioService: MunicipioService
  ) {
    super();
  }

  // A URL base para o controller ImpressaoPessoa.
  private readonly apiUrl = `${environment.urlApiPessoa}ImpressaoPessoa`;

  /**
   * Obtém lista simples de pessoas para impressão.
   * Corresponde a: [HttpPost("lista-simples")]
   * @param request Filtros de busca (PessoaListaRequest).
   */
  public getListaSimples(request: PessoaListaRequest): Observable<GetImpressaoPessoaSimplesResponse> {
    const url = `${this.apiUrl}/lista-simples`;

    return this.httpClient
      .post<GetImpressaoPessoaSimplesResponse>(url, request, this.addHeaderToken())
      .pipe(
        switchMap(response => {
          if (response.isError) return of(response);

          const ids = [...new Set((response.listaPessoasImpressao?.map(item => item.idMunicipio) ?? [])
            .filter((id): id is number => !!id))];
          if (!ids.length) return of(response);

          return this.municipioService.getByIds(ids).pipe(map(municipioResponse => {
            if (municipioResponse.isError) {
              response.isError = true;
              response.errorDescription = municipioResponse.errorDescription || 'Não foi possível consultar os municípios para impressão.';
              return response;
            }

            const municipios = new Map(municipioResponse.list.map(item => [item.idMunicipio, item]));
            if (ids.some(id => !municipios.has(id))) {
              response.isError = true;
              response.errorDescription = 'Não foi possível resolver todos os municípios necessários para impressão.';
              return response;
            }

            response.listaPessoasImpressao.forEach(item => {
              const municipio = item.idMunicipio ? municipios.get(item.idMunicipio) : undefined;
              item.cidade = municipio?.nome ?? '';
              item.uf = municipio?.uf ?? '';
            });
            return response;
          }));
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Obtém lista agrupada de pessoas para impressão (por Estado, Cidade, etc).
   * Corresponde a: [HttpPost("lista-agrupada")]
   * @param request Filtros de busca e tipo de agrupamento.
   */
  public getListaAgrupada(request: PessoaImpressaoAgrupadaRequest): Observable<GetImpressaoPessoaAgrupadaResponse> {
    const url = `${this.apiUrl}/lista-agrupada`;

    return this.httpClient
      .post<GetImpressaoPessoaAgrupadaResponse>(url, request, this.addHeaderToken())
      .pipe(
        switchMap(response => {
          if (response.isError) return of(response);

          const ids = [...new Set((response.listaPessoaImpressaoAgrupada?.flatMap(grupo => [
            grupo.idMunicipio,
            ...grupo.itens.map(item => item.idMunicipio)
          ]) ?? []).filter((id): id is number => !!id))];
          if (!ids.length) return of(response);

          return this.municipioService.getByIds(ids).pipe(map(municipioResponse => {
            if (municipioResponse.isError) {
              response.isError = true;
              response.errorDescription = municipioResponse.errorDescription || 'Não foi possível consultar os municípios para impressão.';
              return response;
            }

            const municipios = new Map(municipioResponse.list.map(item => [item.idMunicipio, item]));
            if (ids.some(id => !municipios.has(id))) {
              response.isError = true;
              response.errorDescription = 'Não foi possível resolver todos os municípios necessários para impressão.';
              return response;
            }

            response.listaPessoaImpressaoAgrupada.forEach(grupo => {
              if (!grupo.idMunicipio) return;
              const municipio = municipios.get(grupo.idMunicipio);
              if (!municipio) return;

              grupo.titulo = request.tipoAgrupamento === 1
                ? municipio.uf ?? 'UF N/D'
                : request.tipoAgrupamento === 2
                  ? `${municipio.uf ?? 'UF N/D'} - ${municipio.nome}`
                  : `${municipio.uf ?? 'UF N/D'} - ${municipio.nome} - ${grupo.bairro ?? ''}`;

              grupo.itens.forEach(item => {
                const municipioItem = item.idMunicipio ? municipios.get(item.idMunicipio) : undefined;
                item.cidade = municipioItem?.nome ?? '';
                item.uf = municipioItem?.uf ?? '';
              });
            });
            response.listaPessoaImpressaoAgrupada.sort((a, b) => a.titulo.localeCompare(b.titulo));
            return response;
          }));
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Obtém lista de aniversariantes.
   * Corresponde a: [HttpPost("lista-aniversariantes")]
   * @param request Filtros de data (dia/mês inicio e fim).
   */
  public getAniversariantes(request: PessoaImpressaoAniversarianteRequest): Observable<GetImpressaoPessoaSimplesResponse> {
    const url = `${this.apiUrl}/lista-aniversariantes`;

    return this.httpClient
      .post<GetImpressaoPessoaSimplesResponse>(url, request, this.addHeaderToken())
      .pipe(
        switchMap(response => {
          if (response.isError) return of(response);

          const ids = [...new Set((response.listaPessoasImpressao?.map(item => item.idMunicipio) ?? [])
            .filter((id): id is number => !!id))];
          if (!ids.length) return of(response);

          return this.municipioService.getByIds(ids).pipe(map(municipioResponse => {
            if (municipioResponse.isError) {
              response.isError = true;
              response.errorDescription = municipioResponse.errorDescription || 'Não foi possível consultar os municípios para impressão.';
              return response;
            }

            const municipios = new Map(municipioResponse.list.map(item => [item.idMunicipio, item]));
            if (ids.some(id => !municipios.has(id))) {
              response.isError = true;
              response.errorDescription = 'Não foi possível resolver todos os municípios necessários para impressão.';
              return response;
            }

            response.listaPessoasImpressao.forEach(item => {
              const municipio = item.idMunicipio ? municipios.get(item.idMunicipio) : undefined;
              item.cidade = municipio?.nome ?? '';
              item.uf = municipio?.uf ?? '';
            });
            return response;
          }));
        }),
        catchError(this.handleError)
      );
  }
}
